<template>
    <v-dialog
        :model-value="modelValue"
        max-width="560"
        :fullscreen="$vuetify.display.smAndDown"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card>
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text>
                <p v-if="description" class="text-body-2 text-medium-emphasis mb-4">
                    {{ description }}
                </p>

                <template v-if="otherOnly">
                    <v-alert
                        class="mb-0"
                        type="info"
                        variant="tonal"
                        density="compact"
                    >
                        <strong>{{ otherCount }}</strong>
                        {{ otherCount === 1 ? 'produs' : 'produse' }}
                        fără modificări de preț
                        {{ otherCount === 1 ? 'va fi aplicat' : 'vor fi aplicate' }}
                        în ultimele <strong>{{ lastFiveCount }}</strong> liste
                        de preț (inclusiv lista curentă).
                    </v-alert>
                </template>

                <template v-else>
                    <v-alert
                        class="mb-4"
                        type="info"
                        variant="tonal"
                        density="compact"
                    >
                        <div v-if="priceCount" class="mb-1">
                            <strong>{{ priceCount }}</strong>
                            {{ priceCount === 1 ? 'produs are' : 'produse au' }}
                            modificări de preț (intrare sau vânzare).
                        </div>
                        <div v-if="otherCount">
                            <strong>{{ otherCount }}</strong>
                            {{ otherCount === 1 ? 'produs are' : 'produse au' }}
                            alte modificări (nu de preț) — doar acestea se aplică
                            în ultimele {{ lastFiveCount }} liste
                            <template v-if="mode === 'new'"> și în lista nouă</template>.
                        </div>
                        <div v-else-if="priceCount" class="text-medium-emphasis">
                            Doar modificări de preț: nu se aplică în ultimele liste.
                        </div>
                    </v-alert>

                    <p class="text-body-2 mb-2">
                        Unde aplicați produsele cu modificări de preț?
                    </p>
                    <v-radio-group
                        v-model="mode"
                        :disabled="loading || resolvingTarget"
                        hide-details="auto"
                        class="mt-0"
                    >
                        <v-radio
                            value="current"
                            label="În lista curentă"
                        />
                        <v-radio
                            value="new"
                            label="Într-o listă nouă"
                        />
                    </v-radio-group>

                    <div v-if="mode === 'new'" class="mt-4">
                        <v-locale-provider locale="ro">
                            <v-text-field
                                v-model="dateApplied"
                                label="Data aplicării"
                                density="compact"
                                type="date"
                                :variant="fieldVariant"
                                clearable
                                hide-details="auto"
                                :disabled="loading || resolvingTarget"
                                @update:model-value="checkExistingPriceList"
                            />
                            <v-text-field
                                v-model="validFromDate"
                                class="mt-4"
                                label="Valabil din (opțional)"
                                density="compact"
                                type="date"
                                :variant="fieldVariant"
                                clearable
                                hide-details="auto"
                                :disabled="loading || resolvingTarget"
                            />
                        </v-locale-provider>
                        <v-alert
                            v-if="showExistingPriceListError"
                            class="mt-4"
                            density="compact"
                            type="warning"
                            title="Atenție"
                            closable
                            close-label="Închide"
                            text="Există deja o listă pentru această dată. Modificările de preț vor fi aplicate pe lista existentă."
                        />
                        <v-alert
                            v-else-if="copyFromPriceListId"
                            class="mt-4"
                            density="compact"
                            type="info"
                            variant="tonal"
                        >
                            Lista nouă preia produsele din lista curentă. Ce e în lista în lucru le actualizează sau le adaugă.
                        </v-alert>
                    </div>
                </template>

                <div v-if="loading && progressTotal > 0" class="mt-4">
                    <p class="text-body-2 mb-2">
                        Se salvează… {{ progressCurrent }} / {{ progressTotal }}
                    </p>
                    <v-progress-linear
                        :model-value="progressPercent"
                        color="primary"
                        height="10"
                        rounded
                    />
                </div>
            </v-card-text>
            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn
                    color="danger"
                    variant="elevated"
                    :disabled="loading"
                    @click="close"
                >
                    Anulează
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important;"
                    :loading="resolvingTarget || loading"
                    :disabled="resolvingTarget || loading || !canConfirm"
                    @click="confirm"
                >
                    {{ confirmLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { createPriceList } from '@/utils/priceListApi';

export default {
    name: 'PriceListTargetDialog',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        priceLists: {
            type: Array,
            default: () => [],
        },
        priceListType: {
            type: String,
            default: 'general',
        },
        defaultPriceListId: {
            type: [Number, String, null],
            default: null,
        },
        copyFromPriceListId: {
            type: [Number, String, null],
            default: null,
        },
        fieldVariant: {
            type: String,
            default: 'outlined',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        progress: {
            type: Object,
            default: null,
        },
        title: {
            type: String,
            default: 'Aplică lista în lucru',
        },
        description: {
            type: String,
            default: '',
        },
        confirmLabel: {
            type: String,
            default: 'Aplică',
        },
        priceCount: {
            type: Number,
            default: 0,
        },
        otherCount: {
            type: Number,
            default: 0,
        },
        lastFiveCount: {
            type: Number,
            default: 5,
        },
    },
    emits: ['update:modelValue', 'confirm'],
    data() {
        return {
            mode: 'new',
            dateApplied: null,
            validFromDate: null,
            showExistingPriceListError: false,
            existingListIdForDate: null,
            resolvingTarget: false,
        };
    },
    computed: {
        otherOnly() {
            return Number(this.priceCount) <= 0 && Number(this.otherCount) > 0;
        },
        progressCurrent() {
            return Number(this.progress?.current) || 0;
        },
        progressTotal() {
            return Number(this.progress?.total) || 0;
        },
        progressPercent() {
            if (!this.progressTotal) {
                return 0;
            }
            return Math.min(
                100,
                Math.round((this.progressCurrent / this.progressTotal) * 100),
            );
        },
        canConfirm() {
            if (this.otherOnly) {
                return true;
            }
            if (this.mode === 'current') {
                return this.defaultPriceListId != null && this.defaultPriceListId !== '';
            }
            return Boolean(this.dateApplied);
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.resetState();
            }
        },
    },
    methods: {
        todayIsoDate() {
            const today = new Date();
            const pad = (value) => String(value).padStart(2, '0');
            return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        },
        resetState() {
            this.mode = this.otherOnly ? 'other_only' : 'new';
            this.dateApplied = this.todayIsoDate();
            this.validFromDate = null;
            this.showExistingPriceListError = false;
            this.existingListIdForDate = null;
            this.checkExistingPriceList(this.dateApplied);
        },
        close() {
            this.$emit('update:modelValue', false);
        },
        checkExistingPriceList(dateApplied) {
            const existingList = this.priceLists.find(
                (priceList) => priceList.date_applied === dateApplied,
            );
            if (existingList) {
                this.showExistingPriceListError = true;
                this.existingListIdForDate = existingList.id;
            } else {
                this.showExistingPriceListError = false;
                this.existingListIdForDate = null;
            }
        },
        async resolveNewPriceListId() {
            if (this.existingListIdForDate != null) {
                return {
                    priceListId: this.existingListIdForDate,
                    createdNewList: false,
                };
            }
            if (!this.dateApplied) {
                return { priceListId: null, createdNewList: false };
            }
            const createdList = await createPriceList({
                type: this.priceListType,
                dateApplied: this.dateApplied,
                validFrom: this.validFromDate,
                copyFromPriceListId: this.copyFromPriceListId,
            });
            return {
                priceListId: createdList?.id || null,
                createdNewList: Boolean(createdList?.id),
            };
        },
        async confirm() {
            this.resolvingTarget = true;
            try {
                if (this.otherOnly) {
                    this.$emit('confirm', {
                        mode: 'other_only',
                        priceListId: this.defaultPriceListId,
                        priceListIds: [],
                        createdNewList: false,
                        createdPriceListId: null,
                    });
                    return;
                }
                if (this.mode === 'current') {
                    this.$emit('confirm', {
                        mode: 'current',
                        priceListId: this.defaultPriceListId,
                        priceListIds: [this.defaultPriceListId],
                        createdNewList: false,
                        createdPriceListId: null,
                    });
                    return;
                }
                const { priceListId, createdNewList } = await this.resolveNewPriceListId();
                if (!priceListId) {
                    return;
                }
                this.$emit('confirm', {
                    mode: 'new',
                    priceListId,
                    priceListIds: [priceListId],
                    createdNewList,
                    createdPriceListId: createdNewList ? priceListId : null,
                });
            } finally {
                this.resolvingTarget = false;
            }
        },
    },
};
</script>
